import { isEducationTier } from '@/lib/modalTiers';
import { type ModalId } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { isPacedModal } from './modalPacing';
import { MODAL_TIER_REGISTER } from './modalTierRegister';

/**
 * R5 covers registered T4/T5 surfaces that pacing also covers. Pacing's R5
 * exception (`PACING_EXEMPT_MODAL_IDS`: Routines spotlight, Heidi II Evidence
 * tour) is kept: those are neither withheld nor counted.
 */
const isCoveredByEducationMutex = (modalId: ModalId): boolean =>
  isEducationTier(MODAL_TIER_REGISTER[modalId]) && isPacedModal(modalId);

/**
 * R5 record: the first registered T4/T5 launch journey with a CONFIRMED display this page
 * load. `null` until one confirms. Reaching the queue head is not a display: a
 * head whose anchor never mounts can be dequeued or advance without using the
 * page load's only journey. Confirmations, all through
 * `recordEducationJourneyAtom`:
 *  - a queued close (`recordPacedLaunchModalDismissedAtom`), whatever the flags;
 *  - impression effects for surfaces that close off-queue: Adaptive learning
 *    (via `SessionAdaptiveLearningPopover`) and the V2 layout invite;
 *  - Copy discovery's confirmed render, including inside the T0 loss popover.
 * Writers record facts whatever the flags; only the tier framework's R5 gate
 * (`isWithheldByEducationMutex`) reads it. Untiered surfaces are never counted.
 * `useQueuedModal`'s `skipPacing` closes skip the record too.
 *
 * Known gap: a queue-closed surface that a higher-priority T4 displaces before
 * it closes has not confirmed, so it does not count as a journey.
 *
 * In memory, so "session" here means page load: a reload clears it. Pacing's
 * session counter lives in sessionStorage and survives a reload.
 */
export const educationJourneyModalIdAtom = atom<ModalId | null>(null);

/**
 * R5 verdict for one surface: withheld when the tier framework is on, another
 * registered T4/T5 journey already showed this page load, and this surface is a
 * registered, paced T4/T5 too. Untiered and pacing-exempt surfaces are never
 * withheld. Shared by the queue gate and the Copy toolbar (`useCopyHints`,
 * `CopyHint`, in the UI PR) so the two cannot drift.
 */
export const isWithheldByEducationMutex = (
  frameworkEnabled: boolean,
  journeyId: ModalId | null,
  modalId: ModalId
): boolean =>
  frameworkEnabled &&
  journeyId !== null &&
  journeyId !== modalId &&
  isCoveredByEducationMutex(modalId);

/**
 * Record a confirmed display. The only place that decides tier: first journey
 * wins, and surfaces R5 doesn't cover are ignored.
 */
export const recordEducationJourneyAtom = atom(null, (get, set, modalId: ModalId) => {
  if (get(educationJourneyModalIdAtom) !== null) return;
  if (!isCoveredByEducationMutex(modalId)) return;
  set(educationJourneyModalIdAtom, modalId);
});
